import * as React from 'react'

import { Box, useClassNames, useToggle } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { ShiftManagementRolesDocument } from 'generated/resources/graphql'
import { Moment } from 'moment'

import { observer } from 'mobx-react'

import { getFilterValue } from 'modules/core/components/EmployeeMultiFilters/commonFilters'
import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { getResources } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/getResources'
import { SHIFT_MANAGEMENT_FILTERS } from 'modules/shift_management/hooks/reducers/filter'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import { ShiftsRow } from 'modules/shift_management/lib/types'
import UnassignedShifts from 'modules/shift_management/store/internal/collections/UnassignedShifts'
import type PlannedBreaks from 'modules/time_planning/store/internal/collections/plannedBreaks'

import * as stylesRefs from '../index.vanilla.css'
import AddUnassignedShiftButton from './AddUnassignedShiftButton'
import AddUnassignedShiftModal from './AddUnassignedShiftModal'
import UnassignedShiftsList from './UnassignedShiftsList'

const CoverageShiftsCell = ({
  date,
  plannedBreaks,
  contractVersions,
}: {
  date: Moment
  plannedBreaks: PlannedBreaks
  contractVersions: ContractVersions
}) => {
  const [openAddShiftModal, setOpenAddShiftModal] = React.useState<boolean>(false)

  const onOpenAddShiftModal = () => {
    track(trackingEvents.shiftManagement.addUnassignedShiftStart, {})
    setOpenAddShiftModal(true)
  }

  const onCloseAddShiftModal = () => {
    setOpenAddShiftModal(false)
  }

  const cx = useClassNames(stylesRefs)
  const [isHovering, toggleHovering] = useToggle(false)

  const { data } = useQuery(ShiftManagementRolesDocument)
  const roles = data?.jobCatalog?.rolesConnection?.nodes.map((r) => r) || []

  const [{ shiftsRows }] = useShiftsRows()
  const [{ view }] = useSchedule()
  const [unassignedShifts] = useUnassignedShifts()
  const { filters } = useEmployeeMultiFilters(getFilterValue, LOCAL_STORAGE_KEYS.shiftEmployees)

  const locationFilterValue = filters?.find(
    (f) => f.values.field === SHIFT_MANAGEMENT_FILTERS.employeeLocation
  )?.values?.value
  const filteredUnassignedShifts = !locationFilterValue
    ? unassignedShifts
    : unassignedShifts.filter(
        (unassignedShift) => Number(unassignedShift.locationId) === Number(locationFilterValue)
      )

  const unassignedShiftsCollection = new UnassignedShifts(filteredUnassignedShifts)

  const shiftsByDate = shiftsRows.flatMap((row: ShiftsRow) => {
    const { shifts } = getResources(row, date, view)
    return shifts
  })

  const sortedUnassignedShifts = unassignedShiftsCollection.orderedByDateAndCounter(
    date,
    shiftsByDate
  )

  return (
    <>
      <Box
        height="full"
        border={{
          right: {
            width: 's1',
            style: 'solid',
            color: 'grey300',
          },
        }}
        className={cx('header')}
        background="grey000White"
        padding="s4"
      >
        <Box alignItems="center" height="full" gap="s4" justifyContent="spaceBetween">
          <Box alignItems="center" height="full" width="full">
            {!sortedUnassignedShifts.length ? (
              <Box
                width="full"
                height="full"
                onMouseEnter={toggleHovering(true)}
                onMouseLeave={toggleHovering(false)}
              >
                {isHovering && <AddUnassignedShiftButton onClick={onOpenAddShiftModal} />}
              </Box>
            ) : (
              <UnassignedShiftsList
                date={date}
                unassignedShifts={sortedUnassignedShifts}
                shiftsByDate={shiftsByDate}
                plannedBreaks={plannedBreaks}
                contractVersions={contractVersions}
                roles={roles}
              />
            )}
          </Box>
        </Box>
      </Box>

      {openAddShiftModal && (
        <AddUnassignedShiftModal date={date} onClose={onCloseAddShiftModal} roles={roles} />
      )}
    </>
  )
}

export default observer(CoverageShiftsCell)
